import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import moment from 'moment';
import { BsModalRef, BsModalService, ModalOptions } from 'ngx-bootstrap/modal';
import { NgxPrinterService } from 'ngx-printer';
import { Observable } from 'rxjs/internal/Observable';
import { Subscription } from 'rxjs/internal/Subscription';

import { Dietitian } from '../../../../data/dietitian/domain/dietitian';
import { Patient } from '../../../../data/patient/domain/patient';
import { DiaryType } from '../../../../data/tracker/domain/diary_type/diary_type';
import { AddEncouragementModalComponent } from '../../../dashboard/routes/questions/routes/patients-list/components/add-encouragement-modal/add-encouragement-modal.component';
import { EditComment } from '../diary-day-column/diary-day-column.component';
import { DiaryDayModel } from './diary.day.model';
import {
  AccountViewModel,
  DiaryFacade,
  PatientInfoViewModel,
} from './diary.facade';

export declare type Period = 'week' | 'day';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'msd-diary',
  templateUrl: './diary.component.html',
  styleUrls: ['./diary.component.scss'],
  providers: [DiaryFacade],
  // Be careful with style leak
  encapsulation: ViewEncapsulation.None,
})
export class DiaryComponent implements OnInit, OnDestroy {
  private _patientId: string | undefined;
  @Input() set patientId(patientId: string | undefined) {
    this._patientId = patientId;
    this.loadDiaries();
  }
  get patientId(): string | undefined {
    return this._patientId;
  }

  private _period: Period = 'week';
  @Input() set period(period: Period) {
    if (period == this._period) return;

    this._period = period;
    this.loadDiaries();
  }
  get period(): Period {
    return this._period;
  }

  private _number = 1;
  @Input() set number(number: number) {
    if (number == this._number) return;

    this._number = number;
    this.loadDiaries();
  }
  get number(): number {
    return this._number;
  }

  private _endDate = new Date();
  @Input() set endDate(date: Date | undefined) {
    if (date == this._endDate) return;

    this._endDate = date ?? new Date();
    this.loadDiaries();
  }
  get endDate(): Date {
    return this._endDate;
  }

  @Input() canBePrinted = false;
  @Input() canBeEncouraged = true;
  @Output() onNavigationChange = new EventEmitter<{ [key: string]: unknown }>();

  public diaryTypes: DiaryType[] = [];
  public isLoading = true;
  public error: string | undefined;
  public days: DiaryDayModel[] = [];
  public ajustedStartDate: moment.Moment | undefined;
  public ajustedEndDate: moment.Moment | undefined;
  public bsModalRef?: BsModalRef;
  public patient: Patient | undefined;
  public dietitian: Dietitian | undefined;
  public periodText: string | undefined;
  public isOneDayPeriod = false;
  public diaryCommentLoadingId?: string;

  private accountStatusSubscription?: Subscription;
  private patientStatusSubscription?: Subscription;
  private patientStatus$: Observable<PatientInfoViewModel>;
  private accountStatus$: Observable<AccountViewModel>;

  constructor(
    private modalService: BsModalService,
    private facade: DiaryFacade,
    private printerService: NgxPrinterService,
  ) {
    this.patientStatus$ = this.facade.patientStatus$;
    this.accountStatus$ = this.facade.accountStatus$;

    this.accountStatusSubscription = this.accountStatus$.subscribe((state) => {
      this.dietitian = state.dietitian;
    });

    this.patientStatusSubscription = this.patientStatus$.subscribe((state) => {
      this.patient = state.patient;
      facade
        .findAllDiaryType(
          this.dietitian?.id.toString(),
          this.patient?.id.toString(),
        )
        .then((diaryTypes) => (this.diaryTypes = diaryTypes));
    });
  }

  ngOnDestroy(): void {
    this.accountStatusSubscription?.unsubscribe();
    this.patientStatusSubscription?.unsubscribe();
  }

  ngOnInit(): void {
    this.loadDiaries().then();
  }

  async commentEdit(data: EditComment) {
    this.diaryCommentLoadingId = data.diary.diaryId.id.toString();
    await this.facade.updateDiaryComment(
      data.diary.diaryId.id.toString(),
      data.diary.props,
      data.comment,
    );
    this.diaryCommentLoadingId = undefined;
  }

  print(section: string) {
    this.printerService.printDiv(section);
  }

  sendEncouragement() {
    const initialState: ModalOptions = {
      class: 'modal-lg modal-dialog-centered',
      initialState: {
        target: this.patient,
        dietitian: this.dietitian,
      },
    };
    this.bsModalRef = this.modalService.show(
      AddEncouragementModalComponent,
      initialState,
    );

    if (this.bsModalRef.content instanceof AddEncouragementModalComponent) {
      this.bsModalRef.content.validate.subscribe(() => {
        this.loadDiaries().then();
      });
    }
  }

  async loadDiaries() {
    this.isLoading = true;
    this.error = undefined;

    if (!this.patientId) {
      this.days = [];
      this.isLoading = true;
      this.periodText = undefined;

      return;
    }

    try {
      const unit = this.period == 'week' ? 'week' : 'day';
      this.ajustedEndDate = moment(this.endDate).local().endOf(unit);
      this.ajustedStartDate = this.ajustedEndDate
        .clone()
        .add(-(this.number - 1), unit)
        .startOf(unit);

      this.isOneDayPeriod =
        this.ajustedStartDate?.isSame(this.ajustedEndDate, 'day') === true;

      if (this.isOneDayPeriod) {
        this.periodText = this.ajustedEndDate?.format('DD MMM yyyy');
      } else {
        this.periodText = `${this.ajustedStartDate?.format(
          'DD MMM yyyy',
        )} - ${this.ajustedEndDate?.format('DD MMM yyyy')}`;
      }

      const dates: moment.Moment[] = [];
      const currentDate = this.ajustedStartDate.clone();
      dates.push(currentDate.clone());

      while (this.ajustedEndDate.diff(currentDate, 'days') > 0) {
        currentDate.add(1, 'days');
        dates.push(currentDate.startOf('day').clone());
      }

      const now = moment();

      // TODO: maybe refactor encouragements using an other entity
      const operations: (DiaryDayModel | Promise<DiaryDayModel>)[] = [];
      for (const day of dates) {
        if (day.isAfter(now)) {
          operations.push(
            new DiaryDayModel(day.format('dddd'), day.date(), [], []),
          );
          continue;
        }

        const startDate = day.toDate();
        const endDate = day.endOf('day').toDate();

        operations.push(
          Promise.all([
            this.facade.getPatientEncouragements(
              this.patientId,
              startDate,
              endDate,
            ),
            this.facade.getPatientDiaries(this.patientId, startDate, endDate),
          ]).then((data) => {
            return new DiaryDayModel(
              day.format('dddd'),
              day.date(),
              data[0],
              data[1],
            );
          }),
        );
      }

      this.days = await Promise.all(operations);
    } catch (error: unknown) {
      if (error instanceof Error) {
        this.error = error.message;
      }
    }
    this.isLoading = false;
  }

  loadPreviousPeriod() {
    if (this.isLoading) return;
    if (!this.ajustedEndDate) return;

    const unit = this.period == 'week' ? 'week' : 'day';
    this.endDate = this.ajustedEndDate
      ?.clone()
      .add(-this.number, unit)
      .endOf(unit)
      .toDate();

    this.onNavigationChange.emit({
      period: this.period,
      number: this.number,
      endDate: this.endDate,
    });
  }

  loadNextPeriod() {
    if (this.isLoading) return;
    if (!this.ajustedEndDate) return;

    const unit = this.period == 'week' ? 'week' : 'day';

    const newDate = this.ajustedEndDate
      ?.clone()
      .add(this.number, unit)
      .endOf(unit)
      .toDate();

    this.endDate = newDate;

    this.onNavigationChange.emit({
      period: this.period,
      number: this.number,
      endDate: this.endDate,
    });
  }
}
